import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import ItemLinksBox from "./AdminItemLinks";

// Admin: who runs active searches right now (2026-10-20). Tap a user to see the searches.
const PLAN_LABELS: Record<string, string> = {
  ADMIN: "Админ",
  TRIAL: "Пробный",
  NONE: "Без подписки",
};

function planLabel(p: string) {
  if (!p) return "—";
  if (PLAN_LABELS[p]) return PLAN_LABELS[p];
  return p.endsWith("*") ? `${p.slice(0, -1)} (выдан)` : p;
}

function when(v: string | null) {
  if (!v) return "—";
  return new Date(v).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminActiveSearches() {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [all, setAll] = useState<any>(null);
  const [allError, setAllError] = useState(false);
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(50);

  useEffect(() => {
    api.get("/admin/active-searches").then((r: any) => setData(r.data)).catch(() => setError(true));
    api.get("/admin/all-searches").then((r: any) => setAll(r.data)).catch(() => setAllError(true));
  }, []);

  // 2026-10-29: the admin pauses a search from here (it leaves the active list)
  const pauseSearch = async (e: any, s: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Поставить на паузу «${s.name}»? Клиенту ничего не придёт — при желании напишите ему.`)) return;
    try {
      await api.post(`/admin/searches/${s.id}/status`, { status: "PAUSED" });
      setData((d: any) => ({ ...d, total: d.total - 1,
        items: d.items.map((u: any) => ({ ...u, searches: u.searches.filter((x: any) => x.id !== s.id) })).filter((u: any) => u.searches.length > 0),
        noisy: (d.noisy || []).filter((x: any) => x.id !== s.id) }));
    } catch {
      window.alert("Не получилось поставить на паузу");
    }
  };

  const needle = q.trim().toLowerCase();
  const filtered = (all?.items || []).filter((s: any) => !needle
    || `${s.name} ${s.username || ""} ${s.first_name || ""} ${s.city}`.toLowerCase().includes(needle));

  const chatLink = (u: any) => (u.username ? `https://t.me/${u.username}` : `tg://user?id=${u.telegram_id}`);

  return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Админ-панель</button>
      <h1 style={styles.title}>Активные поиски</h1>
      <ItemLinksBox />

      {error && <div style={styles.hint}>Не удалось загрузить список</div>}
      {!data && !error && <div style={styles.center}><div style={styles.spinner} /></div>}

      {data && (
        <>
          <div style={styles.hint}>{data.total} поисков у {data.users} пользователей. Нажмите на пользователя, чтобы увидеть его поиски.</div>
          {data.noisy && data.noisy.length > 0 && (
            <div style={{ ...styles.userBox, marginTop: "12px" }}>
              <div style={styles.rowTitle}>🔊 Шумные поиски — больше {data.noisy_day} уведомлений за сутки</div>
              {data.noisy.map((s: any) => (
                <div key={s.id} style={styles.noisyRow}>
                  <div style={{ minWidth: 0 }}>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" style={styles.noisyName}>{s.name}</a>
                    <div style={styles.rowHint}>
                      <a href={chatLink(s)} target="_blank" rel="noopener noreferrer" style={styles.linkText}>
                        {s.username ? `@${s.username}` : (s.first_name || `TG: ${s.telegram_id}`)}
                      </a> · {planLabel(s.plan)}
                    </div>
                  </div>
                  <div style={styles.noisyCount}>{s.day}</div>
                </div>
              ))}
              <div style={{ ...styles.rowHint, marginTop: "6px" }}>
                Слишком широкий поиск: человек тонет в уведомлениях и обычно не платит. Стоит написать и помочь сузить.
              </div>
            </div>
          )}
          <div style={{ ...styles.list, marginTop: "12px" }}>
            {data.items.length === 0 && <div style={styles.hint}>Сейчас активных поисков нет</div>}
            {data.items.map((u: any) => (
              <div key={u.user_id} style={styles.userBox}>
                <div style={styles.userRow} onClick={() => setOpen({ ...open, [u.user_id]: !open[u.user_id] })}>
                  <div>
                    <div style={styles.rowTitle}>{u.username ? `@${u.username}` : (u.first_name || `TG: ${u.telegram_id}`)}</div>
                    <div style={styles.rowHint}>{planLabel(u.plan)}</div>
                  </div>
                  <div style={styles.count}>{u.searches.length} {open[u.user_id] ? "▴" : "▾"}</div>
                </div>
                {open[u.user_id] && (
                  <div style={styles.searches}>
                    {u.searches.map((s: any) => (
                      <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" style={styles.searchCard}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "flex-start" }}>
                          <div style={styles.searchName}>{s.name}</div>
                          <button style={styles.pauseBtn} onClick={(e) => pauseSearch(e, s)}>⏸ Пауза</button>
                        </div>
                        <div style={styles.rowHint}>Работает с {when(s.started_at)} · проверен {when(s.last_checked_at)}{s.day !== undefined ? ` · за сутки: ${s.day}` : ""}</div>
                      </a>
                    ))}
                    <div style={styles.actions}>
                      <a href={chatLink(u)} target="_blank" rel="noopener noreferrer" style={styles.linkBtn}>💬 Написать</a>
                      <button style={styles.linkBtn} onClick={() => navigate(`/admin/users/${u.user_id}/searches`)}>Все поиски →</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <h2 style={styles.subtitle}>Все поиски{all ? ` (${all.total})` : ""}</h2>
      {allError && <div style={styles.hint}>Не удалось загрузить все поиски</div>}
      {!all && !allError && <div style={styles.center}><div style={styles.spinner} /></div>}
      {all && (
        <>
          <div style={styles.hint}>{all.total} поисков у {all.users} пользователей, включая остановленные и удалённые. Ваши (админские) не показаны.</div>

          {all.popular.length > 0 && (
            <div style={{ ...styles.userBox, marginTop: "10px" }}>
              <div style={styles.rowTitle}>Что ищут чаще всего</div>
              {all.popular.map((p: any) => (
                <div key={p.query} style={styles.popRow} onClick={() => { setQ(p.query); setShown(50); }}>
                  <span style={styles.popName}>{p.query}</span>
                  <span style={styles.rowHint}>{p.users} чел. · {p.searches} поиск.{p.active ? ` · активно ${p.active}` : ""}</span>
                </div>
              ))}
              <div style={{ ...styles.rowHint, marginTop: "6px" }}>Нажмите на запрос, чтобы отфильтровать список.</div>
            </div>
          )}

          <input style={styles.input} placeholder="Поиск: товар, ник, город" value={q}
                 onChange={(e) => { setQ(e.target.value); setShown(50); }} />
          {needle && <div style={styles.rowHint}>Найдено: {filtered.length} · <span style={styles.linkText} onClick={() => setQ("")}>сбросить</span></div>}

          <div style={{ ...styles.list, marginTop: "8px" }}>
            {filtered.slice(0, shown).map((s: any) => (
              <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" style={styles.searchCard}>
                <div style={styles.allTop}>
                  <span style={styles.searchName}>{s.name || "Без названия"}</span>
                  <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[s.status] || "#9ca3af" }}>{STATUS_TEXT[s.status] || s.status}</span>
                </div>
                <div style={styles.rowHint}>
                  {s.username ? `@${s.username}` : (s.first_name || `TG: ${s.telegram_id}`)} · {planLabel(s.plan)}{s.city ? ` · ${s.city}` : ""} · {when(s.created_at)}
                </div>
              </a>
            ))}
          </div>
          {filtered.length > shown && (
            <button style={styles.moreBtn} onClick={() => setShown(shown + 50)}>Показать ещё ({filtered.length - shown})</button>
          )}
        </>
      )}
    </div>
  );
}

const STATUS_TEXT: Record<string, string> = { ACTIVE: "Активен", PAUSED: "На паузе", EXPIRED: "Истёк", ERROR: "Ошибка", DELETED: "Удалён" };
const STATUS_COLORS: Record<string, string> = { ACTIVE: "#10b981", PAUSED: "#f59e0b", EXPIRED: "#9ca3af", ERROR: "#ef4444", DELETED: "#6b7280" };

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  backBtn: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  title: { fontSize: "22px", fontWeight: 700, margin: "0 0 8px 0", color: "var(--tg-theme-text-color, #000)" },
  center: { display: "flex", justifyContent: "center", padding: "40px 0" },
  spinner: { width: "28px", height: "28px", border: "3px solid #e5e7eb", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" },
  hint: { fontSize: "13px", color: "var(--tg-theme-hint-color, #666)" },
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  userBox: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "12px", padding: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  userRow: { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" },
  rowTitle: { fontSize: "14px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)" },
  rowHint: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)" },
  count: { fontSize: "16px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)" },
  searches: { display: "flex", flexDirection: "column", gap: "6px", marginTop: "10px" },
  pauseBtn: { flexShrink: 0, padding: "3px 8px", borderRadius: "8px", border: "1px solid rgba(127,127,127,0.35)", background: "transparent", color: "var(--tg-theme-text-color, #000)", fontSize: "12px", cursor: "pointer" },
  searchCard: { display: "block", backgroundColor: "var(--tg-theme-bg-color, #f4f4f5)", borderRadius: "10px", padding: "8px 10px", textDecoration: "none" },
  searchName: { fontSize: "13px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)", marginBottom: "2px" },
  actions: { display: "flex", gap: "12px", marginTop: "4px" },
  subtitle: { fontSize: "18px", fontWeight: 700, margin: "24px 0 6px 0", color: "var(--tg-theme-text-color, #000)" },
  popRow: { display: "flex", justifyContent: "space-between", gap: "8px", padding: "5px 0", cursor: "pointer", borderBottom: "1px solid rgba(127,127,127,0.12)" },
  popName: { fontSize: "13px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)" },
  input: { width: "100%", boxSizing: "border-box", marginTop: "12px", padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(127,127,127,0.3)", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)", fontSize: "14px" },
  allTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" },
  badge: { fontSize: "11px", color: "#fff", padding: "2px 8px", borderRadius: "10px", whiteSpace: "nowrap" },
  linkText: { color: "var(--tg-theme-link-color, #2481cc)", cursor: "pointer", textDecoration: "none" },
  noisyRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", padding: "7px 0", borderBottom: "1px solid rgba(127,127,127,0.12)" },
  noisyName: { fontSize: "13px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)", textDecoration: "none", wordBreak: "break-word" },
  noisyCount: { fontSize: "16px", fontWeight: 700, color: "#ef4444", flexShrink: 0 },
  moreBtn: { width: "100%", padding: "10px", borderRadius: "10px", border: "none", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", marginTop: "8px" },
  linkBtn: { background: "none", border: "none", padding: 0, color: "var(--tg-theme-link-color, #2481cc)", fontSize: "13px", cursor: "pointer", textDecoration: "none" },
};
